import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  EyeIcon,
  PlusIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  CubeIcon,
  ArrowPathIcon,
  PhoneIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import Button from "../../../components/controls/Button";
import DataGrid from "../../../components/Datagrid";
import FormInput from "../../../components/controls/FormInput";
import TutorialModal from "../../../components/ui/TutorialModal";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import ToggleSwitch from "../../../components/ui/Toggle";

// Modal pour gérer les disponibilités/horaires
function AvailabilityModal({ isOpen, onClose, item }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    if (isOpen && item) {
      loadTemplates();
    }
  }, [isOpen, item]);

  const tutorialSteps = [
    {
      title: "Comment ça fonctionne",
      content: "Comment ça fonctionne?",
      tips: [
        "Définissez les créneaux récurrents par jour de la semaine",
        "Capacité : Nombre de réservations simultanées possibles",
        "Surplus : Optionnel, pour ajouter un surplus sur une plage horaire",
        "Vos disponibilités seront automatiquement répétées à chaque semaine, sauf si vous les modifiez",
      ],
    },
  ];

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/inventory/services/${item.id}/availability-templates`
      );
      if (response.ok) {
        const data = await response.json();

        // Grouper les templates par jour
        const groupedByDay = [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
          dayOfWeek,
          slots: data
            .filter((t) => t.dayOfWeek === dayOfWeek)
            .map((t) => ({
              id: t.id,
              startTime: t.startTime,
              endTime: t.endTime,
              maxBookings: t.maxBookings,
              priceOverride: t.priceOverride,
              validFrom: t.validFrom,
              validUntil: t.validUntil,
              isActive: t.isActive,
            })),
        }));

        setTemplates(groupedByDay);
      } else {
        // Initialiser avec des jours vides
        setTemplates(
          [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
            dayOfWeek,
            slots: [],
          }))
        );
      }
    } catch (error) {
      console.error("Erreur chargement templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const addTimeSlot = (dayOfWeek) => {
    setTemplates((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              slots: [
                ...day.slots,
                {
                  startTime: "09:00",
                  endTime: "17:00",
                  maxBookings: 1,
                  priceOverride: null,
                  validFrom: null,
                  validUntil: null,
                  isActive: true,
                },
              ],
            }
          : day
      )
    );
  };

  const removeTimeSlot = (dayOfWeek, slotIndex) => {
    setTemplates((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              slots: day.slots.filter((_, idx) => idx !== slotIndex),
            }
          : day
      )
    );
  };

  const updateTimeSlot = (dayOfWeek, slotIndex, field, value) => {
    setTemplates((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              slots: day.slots.map((slot, idx) =>
                idx === slotIndex ? { ...slot, [field]: value } : slot
              ),
            }
          : day
      )
    );
  };

  const handleSave = async () => {
    try {
      // Aplatir les templates pour l'API
      const flatTemplates = templates.flatMap((day) =>
        day.slots.map((slot) => ({
          dayOfWeek: day.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
          maxBookings: slot.maxBookings,
          priceOverride: slot.priceOverride,
          validFrom: slot.validFrom,
          validUntil: slot.validUntil,
          isActive: true,
        }))
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/inventory/services/${item.id}/availability-templates`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(flatTemplates),
        }
      );

      if (!response.ok) throw new Error("Erreur sauvegarde templates");

      alert("Disponibilités sauvegardées avec succès");
      onClose();
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde des disponibilités");
    }
  };

  const dayNames = {
    1: "Lundi",
    2: "Mardi",
    3: "Mercredi",
    4: "Jeudi",
    5: "Vendredi",
    6: "Samedi",
    0: "Dimanche",
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-5xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
          Templates de disponibilité - {item?.name}{" "}
          <InformationCircleIcon
            className="w-6 h-6 cursor-pointer transition-colors"
            onClick={() => setShowTutorial(true)}
          />
        </h3>

        {loading ? (
          <div className="text-center py-8">
            <div className="loading loading-spinner loading-lg"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {templates.map((day) => (
              <div
                key={day.dayOfWeek}
                className="card bg-base-50 border border-black-500 shadow-lg"
              >
                <div className="card-body p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-lg text-primary">
                      {dayNames[day.dayOfWeek]}
                    </h4>

                    <Button
                      as="button"
                      type="button"
                      onClick={() => addTimeSlot(day.dayOfWeek)}
                      color="primary"
                      size="sm"
                      variant="outline"
                      rightIcon={<PlusIcon className="w-4 h-4" />}
                    >
                      Ajouter un créneau
                    </Button>
                  </div>

                  {day.slots.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 italic">
                      Aucun créneau défini - Service fermé ce jour
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {day.slots.map((slot, slotIndex) => (
                        <div
                          key={slotIndex}
                          className="p-4 bg-yellow-50 border-l-4 border-yellow-400  shadow-lg"
                        >
                          {/* En-tête du créneau */}
                          <div className="flex items-center justify-between pb-2 border-b border-base-300">
                            <span className="font-semibold text-sm text-gray-700">
                              Créneau {slotIndex + 1}
                            </span>

                            <Button
                              as="delete"
                              type="button"
                              onClick={() =>
                                removeTimeSlot(day.dayOfWeek, slotIndex)
                              }
                              color="mint"
                              size="sm"
                              variant="outline"
                              className="color-red-600"
                              rightIcon={<TrashIcon className="w-4 h-4" />}
                            >
                              Supprimer
                            </Button>
                          </div>

                          {/* Horaires */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                              <label className="label">
                                <span className="label-text font-medium">
                                  Heure de début
                                </span>
                              </label>
                              <input
                                type="time"
                                className="input input-bordered w-full"
                                value={slot.startTime}
                                onChange={(e) =>
                                  updateTimeSlot(
                                    day.dayOfWeek,
                                    slotIndex,
                                    "startTime",
                                    e.target.value
                                  )
                                }
                              />
                            </div>

                            <div className="form-control">
                              <label className="label">
                                <span className="label-text font-medium">
                                  Heure de fin
                                </span>
                              </label>
                              <input
                                type="time"
                                className="input input-bordered w-full"
                                value={slot.endTime}
                                onChange={(e) =>
                                  updateTimeSlot(
                                    day.dayOfWeek,
                                    slotIndex,
                                    "endTime",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </div>

                          {/* Capacité et Prix */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                              <label className="label">
                                <span className="label-text font-medium">
                                  Capacité
                                </span>
                                <span className="label-text-alt text-gray-500">
                                  Réservations max.
                                </span>
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="100"
                                className="input input-bordered w-full"
                                value={slot.maxBookings}
                                onChange={(e) =>
                                  updateTimeSlot(
                                    day.dayOfWeek,
                                    slotIndex,
                                    "maxBookings",
                                    parseInt(e.target.value) || 1
                                  )
                                }
                              />
                            </div>

                            <div className="form-control">
                              <label className="label">
                                <span className="label-text font-medium">
                                  Prix override
                                </span>
                                <span className="label-text-alt text-gray-500">
                                  Optionnel
                                </span>
                              </label>
                              <label className="input-group">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="Prix par défaut"
                                  className="input input-bordered w-full"
                                  value={slot.priceOverride || ""}
                                  onChange={(e) =>
                                    updateTimeSlot(
                                      day.dayOfWeek,
                                      slotIndex,
                                      "priceOverride",
                                      e.target.value
                                        ? parseFloat(e.target.value)
                                        : null
                                    )
                                  }
                                />
                                <span>$</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="modal-action mt-6">
          <Button
            as="button"
            type="button"
            onClick={onClose}
            color="primary"
            size="sm"
            variant="outline"
          >
            Annuler
          </Button>
          <Button
            as="button"
            type="button"
            onClick={handleSave}
            color="primary"
            size="sm"
            disabled={loading}
          >
            Enregistrer les templates
          </Button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
      <TutorialModal
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        steps={tutorialSteps}
      />
    </div>
  );
}

// Modal pour créer/éditer un élément
function ItemModal({ isOpen, onClose, item, type }) {
  if (!isOpen) return null;

  const isEdit = !!item;
  const title = isEdit
    ? `Modifier ${type === "product" ? "le produit" : "le service"}`
    : `Nouveau ${type === "product" ? "produit" : "service"}`;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <PlusIcon className="w-6 h-6" />
          {title}
        </h3>
        <div className="py-4">
          <p className="text-gray-600">
            Formulaire de {isEdit ? "modification" : "création"} d'un élément.
          </p>
        </div>
        <div className="modal-action">
          <Button
            as="button"
            type="button"
            onClick={onClose}
            color="primary"
            size="sm"
            variant="outline"
          >
            Annuler
          </Button>
          <Button
            as="button"
            type="button"
            onClick={() => {
              onClose();
            }}
            color="primary"
            size="sm"
          >
            {isEdit ? "Mettre à jour" : "Créer"}
          </Button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
}

export default function InventoryManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState("");

  const [availabilityModal, setAvailabilityModal] = useState({
    isOpen: false,
    item: null,
  });
  const [itemModal, setItemModal] = useState({
    isOpen: false,
    item: null,
    type: "product",
  });

  useEffect(() => {
    loadInventory();
  }, [search, filterType]);

  const loadInventory = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/inventory?search=${search}&type=${filterType}`
      );
      if (!response.ok) throw new Error("Erreur chargement inventaire");
      const data = await response.json();
      setItems(data);
    } catch (err) {
      console.error("Erreur:", err);
      setError("Erreur lors du chargement de l'inventaire.");
    } finally {
      setLoading(false);
    }
  };

  const handleCellEdit = async (itemId, field, value) => {
    try {
      let convertedValue = value;

      if (field === "stock") {
        convertedValue =
          value === "" || value === null ? null : parseInt(value, 10);
        if (
          convertedValue !== null &&
          (isNaN(convertedValue) || convertedValue < 0)
        ) {
          alert("Veuillez entrer un nombre entier positif valide");
          return false;
        }
      }

      if (field === "price") {
        convertedValue =
          value === "" || value === null ? null : parseFloat(value);
        if (
          convertedValue !== null &&
          (isNaN(convertedValue) || convertedValue < 0)
        ) {
          alert("Veuillez entrer un prix valide");
          return false;
        }
      }

      if (field === "priceType") {
        convertedValue =
          value === "" || value === null ? null : parseInt(value, 10);
      }

      if (field === "isActive") {
        convertedValue = Boolean(value);
      }

      console.log(`Modification ${field}:`, {
        original: value,
        converted: convertedValue,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/inventory/${itemId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [field]: convertedValue }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Erreur serveur:", errorData);
        throw new Error(errorData.title || "Erreur de sauvegarde");
      }

      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, [field]: convertedValue } : item
        )
      );
      return true;
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde: " + error.message);
      return false;
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;

    const confirmMsg =
      action === "delete"
        ? `Supprimer ${selectedIds.length} élément(s) ?`
        : `${action === "activate" ? "Activer" : "Désactiver"} ${
            selectedIds.length
          } élément(s) ?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/inventory/bulk`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: selectedIds, action }),
        }
      );
      if (!response.ok) throw new Error("Erreur action groupée");

      loadInventory();
      setSelectedIds([]);
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de l'action groupée");
    }
  };

  const openAvailabilityModal = (item) => {
    setAvailabilityModal({ isOpen: true, item });
  };

  const openNewItemModal = (type = "product") => {
    setItemModal({ isOpen: true, item: null, type });
  };

  const openEditItemModal = (item) => {
    setItemModal({ isOpen: true, item, type: item.itemType });
  };

  const columns = [
    {
      key: "itemType",
      label: "Type",
      width: "120px",
      editable: false,
      format: (value) => {
        const translations = {
          product: "Produit",
          service: "Service",
        };
        return translations[value] || value;
      },
    },
    {
      key: "internalReference",
      label: "Réf. interne",
      width: "120px",
      editable: false,
      type: "text",
      format: (value) => value || <span className="text-gray-400">—</span>,
    },
    {
      key: "name",
      label: "Nom",
      width: "250px",
      editable: false,
      type: "text",
    },
    {
      key: "price",
      label: "Prix",
      width: "100px",
      editable: true,
      type: "number",
      step: "0.01",
      min: "0",
      format: (value) => `${value?.toFixed(2) || "0.00"} $`,
    },
    {
      key: "priceType",
      label: "Type prix",
      width: "120px",
      editable: true,
      type: "select",
      options: [
        { value: 1, label: "Fixe" },
        { value: 2, label: "Heure" },
        { value: 3, label: "Jour" },
        { value: 4, label: "Unité" },
      ],
      format: (value) => {
        const labels = { 1: "Fixe", 2: "Heure", 3: "Jour", 4: "Unité" };
        return labels[value] || "—";
      },
    },
    {
      key: "stock",
      label: "Stock",
      width: "120px",
      render: (row, { startEdit, isEditing }) => {
        if (row.itemType === "service") {
          return;
        }

        if (row.inventoryMode === "unlimited") {
          return (
            <span className="text-gray-500 flex items-center gap-1">
              <ArrowPathIcon className="w-5 h-5" />
              <span className="text-xs">∞</span>
            </span>
          );
        }

        if (isEditing) {
          return null;
        }

        const isLowStock = row.stock < 5 && row.inventoryMode === "fixed";
        return (
          <div
            className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-2 rounded transition-colors"
            onClick={startEdit}
            title="Cliquer pour modifier le stock"
          >
            <span className={isLowStock ? "text-orange-600 font-semibold" : ""}>
              {row.stock}
            </span>
            {isLowStock && (
              <ExclamationTriangleIcon
                className="w-4 h-4 text-orange-600"
                title="Stock faible"
              />
            )}
          </div>
        );
      },
      editable: true,
      type: "number",
      min: "0",
    },
    {
      key: "inventoryMode",
      label: "Mode",
      width: "140px",
      render: (row) => {
        if (row.itemType === "service") {
          return (
            <Button
              as="button"
              type="button"
              onClick={() => openAvailabilityModal(row)}
              color="primary"
              size="sm"
              variant="outline"
              className="flex items-center gap-1"
              rightIcon={<ClockIcon className="w-4 h-4" />}
            >
              Horaires
            </Button>
          );
        }

        const modes = {
          fixed: { icon: CubeIcon, label: "Fixe" },
          unlimited: { icon: ArrowPathIcon, label: "Illimité" },
          contact: { icon: PhoneIcon, label: "Contact" },
        };

        const mode = modes[row.inventoryMode];
        if (!mode) return "—";

        const Icon = mode.icon;
        return (
          <span className="flex items-center gap-1">
            <Icon className="w-4 h-4" />
            {mode.label}
          </span>
        );
      },
      editable: true,
      type: "select",
      options: [
        { value: "fixed", label: "Fixe" },
        { value: "unlimited", label: "Illimité" },
        { value: "contact", label: "Contact" },
      ],
    },
    {
      key: "isActive",
      label: "Actif",
      width: "100px",
      cellClassName: "text-center",
      render: (row) => (
        <ToggleSwitch
          isEnabled={row.isActive}
          onToggle={async () => {
            const newValue = !row.isActive;
            const success = await handleCellEdit(row.id, "isActive", newValue);
          }}
          labelOn=""
          labelOff=""
          className="toggle-sm"
        />
      ),
    },
  ];

  const handleExportCSV = () => {
    // Préparer les données pour l'export
    const csvData = items.map((item) => ({
      Type: item.itemType === "product" ? "Produit" : "Service",
      "Réf. interne": item.internalReference || "",
      SKU: item.sku || "",
      Nom: item.name,
      Prix: item.price?.toFixed(2) || "0.00",
      "Type de prix":
        item.priceType === 1
          ? "Fixe"
          : item.priceType === 2
          ? "Heure"
          : item.priceType === 3
          ? "Jour"
          : "Unité",
      Stock: item.stock || "",
      "Mode inventaire": item.inventoryMode || "",
      Actif: item.isActive ? "Oui" : "Non",
    }));

    // Créer les en-têtes CSV
    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        headers
          .map((header) => {
            const value = row[header] || "";
            // Échapper les virgules et guillemets
            return `"${String(value).replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ].join("\n");

    // Créer le blob et télécharger
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `inventaire_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6 space-y-6 text-primary">
      <Button
        as="button"
        type="button"
        onClick={() => navigate(-1)}
        color="primary"
        size="md"
        className="rounded-full flex items-center gap-2"
        leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
      >
        {t("global.back")}
      </Button>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CubeIcon className="w-7 h-7" />
          Gestion d'inventaire et disponibilités
        </h1>
        <div className="relative w-full md:w-80">
          <FormInput
            placeholder="Rechercher..."
            name="search"
            value={search}
            onChange={(name, value) => setSearch(value)}
            type="text"
            icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />}
            showRequiredOrOptional={false}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <FunnelIcon className="w-5 h-5 text-gray-500" />
        <div className="btn-group">
          <button
            onClick={() => setFilterType("all")}
            className={`btn btn-sm m-2 ${
              filterType === "all" ? "btn-active" : ""
            }`}
          >
            Tous
          </button>
          <button
            onClick={() => setFilterType("products")}
            className={`btn btn-sm mr-2 ${
              filterType === "products" ? "btn-active" : ""
            }`}
          >
            Produits
          </button>
          <button
            onClick={() => setFilterType("services")}
            className={`btn btn-sm ${
              filterType === "services" ? "btn-active" : ""
            }`}
          >
            Services
          </button>
        </div>
        <div className="flex-1" />
        <Button
          as="button"
          type="button"
          onClick={handleExportCSV}
          color="primary"
          size="sm"
          variant="outline"
          rightIcon={<ArrowUpTrayIcon className="w-4 h-4 mr-2" />}
        >
          Exporter CSV
        </Button>
      </div>

      {error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : (
        <DataGrid
          columns={columns}
          data={items}
          onCellEdit={handleCellEdit}
          loading={loading}
          emptyMessage="Aucun produit ou service trouvé"
          rowSelection={true}
          onSelectionChange={setSelectedIds}
        />
      )}

      <div className="text-right text-sm text-gray-600">
        {items.length} élément(s) au total
      </div>

      <AvailabilityModal
        isOpen={availabilityModal.isOpen}
        onClose={() => setAvailabilityModal({ isOpen: false, item: null })}
        item={availabilityModal.item}
      />

      <ItemModal
        isOpen={itemModal.isOpen}
        onClose={() =>
          setItemModal({ isOpen: false, item: null, type: "product" })
        }
        item={itemModal.item}
        type={itemModal.type}
      />
    </div>
  );
}
